import { extractToolCallStatus, humanizeToolName } from "#components/ui/tool-approval/toolApprovalDecision.ts";
import { getToolNameKey } from "#features/conversations/types/assistantError.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { PendingApprovalCall } from "#types/ToolApproval.ts";
import { Div, Flex, Text } from "@hopper-ui/components";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";

interface Props {
    call: PendingApprovalCall;
    isBatch: boolean;
}

// Limited-markdown rendering for status_detail (which wraps parameter values in **bold**):
// inline emphasis only — never block elements or raw HTML — and unwrap the wrapping <p> so the
// sentence sits inline in the muted line.
const STATUS_DETAIL_MARKDOWN = {
    skipHtml: true,
    allowedElements: ["p", "strong", "em", "code"],
    unwrapDisallowed: true,
    components: {
        p: ({ children }: { children?: ReactNode }) => <>{children}</>
    }
};

/** Today's one-size-fits-all call content: the status title (or tool label) and its status detail. */
export function GenericToolApprovalContent({ call, isBatch }: Props) {
    const { tShared } = useSharedTranslation();
    const { title, detail } = extractToolCallStatus(call.args);

    // Fallback label when the tool didn't emit a status_title: the per-locale tool-name label
    // (`toolNames.<tool>`, shared with thinking steps and errors), humanized for anything unmapped.
    // A destructive approval must still name the action, so we never fall back to a generic string.
    const label = title ?? tShared(getToolNameKey(call.toolName), { defaultValue: humanizeToolName(call.toolName) });

    return (
        <Flex direction="column" gap="inline-xs" minWidth={0}>
            <Text size={isBatch ? "xs" : "md"} fontWeight={isBatch ? undefined : "body-md-medium"} color="neutral">
                {label}
            </Text>
            {detail !== null && (
                <Div fontSize="body-xs" color="neutral-weak" whiteSpace="pre-wrap" wordBreak="break-word">
                    <ReactMarkdown {...STATUS_DETAIL_MARKDOWN}>{detail}</ReactMarkdown>
                </Div>
            )}
        </Flex>
    );
}
